import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { spawnInkSplatter, spawnSporeBlob } from './particles'
import { currentTier } from '../evolution'
import { T } from '../tunables'

/** A short-lived spore cloud that expands then fades. Slows + lightly damages. */
export function spawnSporeCloud(world: GameWorld, x: number, y: number): void {
  const c = T.spore
  world.add({
    position: { x, y },
    // Pale spore-lavender so the cloud reads against the brown mushroom floor.
    renderable: { shape: 'toxic', radius: c.startRadius, tint: [0.86, 0.82, 0.95] },
    hazard: {
      kind: 'spore',
      damage: c.dps,
      tickInterval: 0.3,
      tickTimer: 0,
      life: c.life,
      maxLife: c.life,
      maxRadius: c.maxRadius,
    },
  })
}

// Per-hazard ambient spore emission cadence — keep loose so the same toxic
// pool doesn't pulse at a perceptible rhythm. ~one spore per 1.4–2.4s per pool.
const sporeNextEmit = new WeakMap<Entity, number>()

export function hazardSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  const shielded = !!(player.shield && player.shield.activeRemaining > 0)
  const iFrame = (player.invincible ?? 0) > 0
  const playerR = player.renderable?.radius ?? 0.12

  const expiredSpores: Entity[] = []
  for (const h of world.with('hazard', 'position', 'renderable')) {
    // Spore cloud: expand over the first phase, hold, then fade; expire on life.
    if (h.hazard.kind === 'spore') {
      h.hazard.life = (h.hazard.life ?? 0) - dt
      if (h.hazard.life <= 0) { expiredSpores.push(h); continue }
      const t = 1 - h.hazard.life / (h.hazard.maxLife ?? 1) // 0 → 1 over life
      const grow = Math.min(1, t / 0.35)                    // full size at 35% of life
      h.renderable.radius = (h.hazard.maxRadius ?? 0.9) * (0.25 + 0.75 * grow)
    }

    // Ambient spore drift from toxic pools — runs regardless of player range
    // so the world feels alive even when you're not standing in it.
    if (h.hazard.kind === 'toxic') {
      const next = sporeNextEmit.get(h) ?? 0
      const newNext = next - dt
      if (newNext <= 0) {
        const tint = h.renderable.tint
        const angle = Math.random() * Math.PI * 2
        const dist  = h.renderable.radius * (0.4 + Math.random() * 0.4)
        spawnSporeBlob(
          world,
          h.position.x + Math.cos(angle) * dist,
          h.position.y + Math.sin(angle) * dist,
          tint,
        )
        sporeNextEmit.set(h, 1.4 + Math.random() * 1.0)
      } else {
        sporeNextEmit.set(h, newNext)
      }
    }

    const r = h.renderable.radius + playerR
    const dx = h.position.x - player.position.x
    const dy = h.position.y - player.position.y
    const inside = dx * dx + dy * dy < r * r

    if (!inside) {
      // Reset toxic tick timer when player leaves zone
      if (h.hazard.kind === 'toxic') h.hazard.tickTimer = 0
      continue
    }

    if (h.hazard.kind === 'spike') {
      if (!shielded && !iFrame && player.health) {
        // Guard scales hazard damage too — one incoming-damage stat, no exceptions.
        const dmg = (h.hazard.damage ?? 1) * (player.mods?.damageTakenMult ?? 1)
        player.health.current -= dmg
        player.invincible = 1.2
        player.hitFlash = 0.18
        events.emit({ type: 'damage.taken', entity: player, amount: dmg })
        spawnInkSplatter(world, player.position.x, player.position.y)
      }
    } else if (h.hazard.kind === 'toxic') {
      if (shielded) continue
      // Toxic bypasses normal i-frames — DoT ticks on its own cadence
      h.hazard.tickTimer = (h.hazard.tickTimer ?? 0) + dt
      const interval = h.hazard.tickInterval ?? 0.25
      if (h.hazard.tickTimer >= interval && player.health) {
        h.hazard.tickTimer -= interval
        const dmg = (h.hazard.damage ?? 0.5) * (player.mods?.damageTakenMult ?? 1)
        player.health.current -= dmg
        player.hitFlash = 0.12
        events.emit({ type: 'damage.taken', entity: player, amount: dmg })
      }
    } else if (h.hazard.kind === 'current') {
      const imp = h.hazard.impulse
      if (imp && player.velocity) {
        player.velocity.x += imp.x * dt
        player.velocity.y += imp.y * dt
      }
    } else if (h.hazard.kind === 'spore') {
      // Spore cloud: slow the player + a light DoT. Not blocked by i-frames
      // (it's an impairment, not a hit), but a shield keeps you clean.
      if (shielded) continue
      player.slowRemaining = Math.max(player.slowRemaining ?? 0, T.spore.slowLinger)
      // Tier-3+ specimens shrug off the toxin (still slowed, gentler — movement.ts).
      if (currentTier(player) >= T.spore.resilientTier) continue
      h.hazard.tickTimer = (h.hazard.tickTimer ?? 0) + dt
      const interval = h.hazard.tickInterval ?? 0.3
      if (h.hazard.tickTimer >= interval && player.health) {
        h.hazard.tickTimer -= interval
        const dmg = h.hazard.damage ?? 0.3
        player.health.current -= dmg
        player.hitFlash = 0.1
        events.emit({ type: 'damage.taken', entity: player, amount: dmg })
      }
    }
  }

  for (const h of expiredSpores) {
    if (world.id(h) !== undefined) world.remove(h)
  }
}
